import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FiMic, 
  FiMessageSquare, 
  FiZap, 
  FiShield, 
  FiGlobe,
  FiHeadphones,
  FiCpu
} from 'react-icons/fi';

const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  background: rgba(10, 10, 15, 0.5);
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(100, 150, 255, 0.1);
  border: 1px solid rgba(100, 150, 255, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6496FF;
  margin-bottom: 1.5rem;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #E8EAF0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureCard = styled(motion.div)`
  background: rgba(25, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(100, 150, 255, 0.3);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(100, 150, 255, 0.5), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, rgba(100, 150, 255, 0.2) 0%, rgba(70, 120, 255, 0.2) 100%);
  border: 1px solid rgba(100, 150, 255, 0.3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #6496FF;
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #FFFFFF;
`;

const FeatureDescription = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.5rem;
  
  &::before {
    content: '✓';
    color: #6496FF;
    font-weight: bold;
  }
`;

const Features = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const features = [
    {
      icon: <FiMic />,
      title: "Listens Silently",
      description: "Your AI teammate sits quietly in every meeting, understanding context without disrupting the flow.",
      items: [
        "No setup required",
        "Continuous listening",
        "Context awareness",
        "Zero interruption"
      ]
    },
    {
      icon: <FiCpu />,
      title: "Instant Answers",
      description: "Get immediate responses to your questions without fumbling through notes or stalling conversations.",
      items: [
        "Real-time responses",
        "No context explanation needed",
        "Instant clarifications",
        "Smart suggestions"
      ]
    },
    {
      icon: <FiGlobe />,
      title: "Works Everywhere",
      description: "Ready for Zoom, Teams, Google Meet, or any meeting platform on macOS and Windows.",
      items: [
        "All major platforms",
        "macOS & Windows",
        "No integration needed",
        "Universal compatibility"
      ]
    },
    {
      icon: <FiHeadphones />,
      title: "High-Stakes Support",
      description: "Perfect for consultants, sales engineers, and executives who need to respond with confidence.",
      items: [
        "Handle tough questions",
        "Maintain credibility",
        "Never lose momentum",
        "Stay in control"
      ]
    },
    {
      icon: <FiMessageSquare />,
      title: "Context Understanding",
      description: "Already knows what's happening in your meeting — just ask and get the answer you need.",
      items: [
        "No explanation needed",
        "Understands meeting flow",
        "Tracks key points",
        "Remembers decisions"
      ]
    },
    {
      icon: <FiShield />,
      title: "Total Confidence",
      description: "Never fumble, guess, or lose trust again. Sound smart even when caught off guard.",
      items: [
        "Instant confidence boost",
        "Professional responses",
        "No more guessing",
        "Maintain expertise"
      ]
    }
  ];

  return (
    <FeaturesSection id="features" ref={ref}>
      <Container>
        <SectionHeader>
          <Badge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <FiZap />
            Powerful Features
          </Badge>
          
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Your Second Brain for Every Call
          </Title>
          
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Whether you're pitching, troubleshooting, aligning, or negotiating — 
            Meeting Copilot helps you sound smart, stay sharp, and never miss a beat.
          </Subtitle>
        </SectionHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                variants={cardVariants}
                whileHover={{ scale: 1.02 }}
              >
                <FeatureIcon>
                  {feature.icon}
                </FeatureIcon>
                
                <FeatureTitle>{feature.title}</FeatureTitle>
                
                <FeatureDescription>
                  {feature.description}
                </FeatureDescription>
                
                <FeatureList>
                  {feature.items.map((item, itemIndex) => (
                    <FeatureListItem key={itemIndex}>
                      {item}
                    </FeatureListItem>
                  ))}
                </FeatureList>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </motion.div>
      </Container>
    </FeaturesSection>
  );
};

export default Features; 